import type { UserRole } from "./auth-contract";
import {
  defaultFormatMessage,
  type FormatMessageFn,
  formatKnownLabel,
  normalizeStringValue,
} from "./exam-enum-format";
import { readExactStringEnumValue } from "./strict-enum";

export const USER_STATUS_VALUES = ["active", "inactive"] as const;

export type UserStatusValue = (typeof USER_STATUS_VALUES)[number];

export const USER_STATUS_DISPLAY_VALUES = ["active", "inactive", "disabled", "locked"] as const;

export type UserStatusDisplayValue = (typeof USER_STATUS_DISPLAY_VALUES)[number];

export const ROLE_CATALOG_STATUS_VALUES = ["active", "inactive"] as const;

export type RoleCatalogStatusValue = (typeof ROLE_CATALOG_STATUS_VALUES)[number];

type UserRoleKeySource = {
  role_keys?: unknown;
};

const USER_ROLE_VALUE_MAP = {
  admin: true,
  question_editor: true,
  exam_manager: true,
  reviewer: true,
  proctor: true,
  candidate: true,
} as const satisfies Record<UserRole, true>;

export const USER_ROLE_VALUES = Object.keys(USER_ROLE_VALUE_MAP) as UserRole[];

const USER_STATUS_LABEL_KEY_MAP: Record<UserStatusDisplayValue, string> = {
  active: "common.enums.userStatus.active",
  inactive: "common.enums.userStatus.inactive",
  disabled: "common.enums.userStatus.disabled",
  locked: "common.enums.userStatus.locked",
};

const USER_STATUS_TAG_COLOR_MAP: Record<
  UserStatusDisplayValue,
  "default" | "success" | "warning" | "error"
> = {
  active: "success",
  inactive: "default",
  disabled: "warning",
  locked: "error",
};

const ROLE_CATALOG_STATUS_LABEL_KEY_MAP: Record<RoleCatalogStatusValue, string> = {
  active: "admin.rolePermissions.status.active",
  inactive: "admin.rolePermissions.status.inactive",
};

const ROLE_CATALOG_STATUS_TAG_COLOR_MAP: Record<RoleCatalogStatusValue, "default" | "success"> = {
  active: "success",
  inactive: "default",
};

function normalizeUserStatusValue(value?: string | null) {
  return normalizeStringValue(value);
}

function normalizeRoleCatalogStatusValue(value?: string | null) {
  return normalizeStringValue(value);
}

export function readUserRoleKeys(
  source: UserRoleKeySource | null | undefined,
  fieldName = "user.role_keys",
): UserRole[] {
  if (!source) {
    throw new Error(`CONTRACT_MISSING:${fieldName}`);
  }
  if (!Array.isArray(source.role_keys)) {
    throw new Error(`CONTRACT_MISSING:${fieldName}`);
  }
  return source.role_keys.map((roleKey, index) =>
    readUserRoleKey(roleKey, `${fieldName}[${index}]`),
  );
}

export function readUserRoleKey(value: unknown, fieldName = "user.role_keys[]"): UserRole {
  return readExactStringEnumValue("Exam user role contract", fieldName, value, USER_ROLE_VALUES);
}

export function getUserStatusLabel(
  value?: string | null,
  formatMessage: FormatMessageFn = defaultFormatMessage,
) {
  return formatKnownLabel(
    normalizeUserStatusValue(value),
    USER_STATUS_LABEL_KEY_MAP,
    formatMessage,
  );
}

export function getUserStatusTagColor(value?: string | null) {
  const normalized = normalizeUserStatusValue(value);
  return USER_STATUS_TAG_COLOR_MAP[normalized as UserStatusDisplayValue] ?? "warning";
}

export function buildUserStatusFilters(formatMessage: FormatMessageFn = defaultFormatMessage) {
  return USER_STATUS_VALUES.map((value) => ({
    text: getUserStatusLabel(value, formatMessage),
    value,
  }));
}

export function buildUserStatusSelectOptions(
  formatMessage: FormatMessageFn = defaultFormatMessage,
) {
  return USER_STATUS_VALUES.map((value) => ({
    label: getUserStatusLabel(value, formatMessage),
    value,
  }));
}

export function getRoleCatalogStatusLabel(
  value?: string | null,
  formatMessage: FormatMessageFn = defaultFormatMessage,
) {
  return formatKnownLabel(
    normalizeRoleCatalogStatusValue(value),
    ROLE_CATALOG_STATUS_LABEL_KEY_MAP,
    formatMessage,
  );
}

export function getRoleCatalogStatusTagColor(value?: string | null) {
  const normalized = normalizeRoleCatalogStatusValue(value);
  return ROLE_CATALOG_STATUS_TAG_COLOR_MAP[normalized as RoleCatalogStatusValue] ?? "warning";
}
